import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  DropdownMenuItem,
  Icon,
  useToast,
} from '@eluve/components';

import { deleteExperimentMutation, getExperimentsQuery } from './operations';

interface DeleteExperimentActionProps {
  experimentId: string;
  experimentName: string;
  isActivated: boolean;
  asMenuItem?: boolean;
}

export const DeleteExperimentAction: React.FC<DeleteExperimentActionProps> = ({
  experimentId,
  experimentName,
  isActivated,
  asMenuItem = false,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const [deleteExperiment] = useMutation(deleteExperimentMutation, {
    variables: { experimentId },
    refetchQueries: [{ query: getExperimentsQuery }],
    onCompleted: () => {
      setOpen(false);
      navigate('/admin/experiments');
    },
    onError: () => {
      toast.error(
        'Failed to delete experiment. Experiments cannot be deleted after activation.',
      );
    },
  });

  const handleDelete = () => {
    deleteExperiment();
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {asMenuItem ? (
          <DropdownMenuItem
            disabled={isActivated}
            onSelect={(e) => e.preventDefault()}
            className="text-text-negative"
          >
            <Icon name="Trash2" className="mr-2" />
            Delete
          </DropdownMenuItem>
        ) : (
          <Button variant="outline" disabled={isActivated}>
            <Icon name="Trash" size="auto" />
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Experiment?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete &quot;{experimentName}&quot;? This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
